import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "ProgramaInstructores";

// =====================================================
// NORMALIZAR TEXTO
// =====================================================
const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

// =====================================================
// LEER LOCALSTORAGE DE FORMA SEGURA
// =====================================================
const leerStorage = (key, valorDefault = {}) => {
  try {
    const contenido = localStorage.getItem(key);

    return contenido
      ? JSON.parse(contenido)
      : valorDefault;
  } catch (error) {
    console.error(
      `Error leyendo ${key} desde localStorage:`,
      error
    );

    return valorDefault;
  }
};

// =====================================================
// BUSCAR UN MÓDULO EN LOS PERMISOS
// =====================================================
const buscarModuloPorNombre = (nombreModulo) => {
  try {
    const fuentes = [
      leerStorage("permisos", {}),
      leerStorage("usuario", {}),
      leerStorage("user", {}),
      leerStorage("usuarios", {}),
    ];

    const moduloBuscado =
      normalizarTexto(nombreModulo);

    for (const fuente of fuentes) {
      const modulos = Array.isArray(fuente)
        ? fuente
        : fuente?.modulos ||
          fuente?.permisos ||
          fuente?.usuario?.modulos ||
          fuente?.usuario?.permisos ||
          fuente?.user?.modulos ||
          fuente?.user?.permisos ||
          [];

      if (!Array.isArray(modulos)) {
        continue;
      }

      const moduloEncontrado = modulos.find(
        (modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            modulo?.name ||
            modulo?.moduloObjectId?.nombreModulo ||
            modulo?.moduloObjectId?.nombre ||
            "";

          return (
            normalizarTexto(nombre) ===
            moduloBuscado
          );
        }
      );

      if (moduloEncontrado) {
        return moduloEncontrado;
      }
    }

    return {};
  } catch (error) {
    console.error(
      "Error buscando el módulo:",
      error
    );

    return {};
  }
};

// =====================================================
// ESTILO DEL ROL EN LA ASIGNACIÓN
// =====================================================
const obtenerEstiloRolInstructor = (
  rolInstructor = ""
) => {
  const rol = String(rolInstructor)
    .trim()
    .toUpperCase();

  const estilos = {
    LIDER: {
      backgroundColor: "#0d6efd",
      color: "#ffffff",
      icono: "fa-solid fa-crown",
      etiqueta: "LÍDER",
    },

    APOYO: {
      backgroundColor: "#fd7e14",
      color: "#ffffff",
      icono: "fa-solid fa-people-group",
      etiqueta: "APOYO",
    },
  };

  return (
    estilos[rol] || {
      backgroundColor: "#6c757d",
      color: "#ffffff",
      icono: "fa-solid fa-circle-question",
      etiqueta: rol || "SIN ROL",
    }
  );
};

// =====================================================
// NOMBRE DEL PROGRAMA
// =====================================================
const obtenerNombrePrograma = (
  programa = {}
) => {
  return (
    programa.nombrePrograma ||
    programa.nombreProgramaFormacion ||
    programa.denominacionPrograma ||
    programa.denominacion ||
    programa.nombre ||
    "Programa no disponible"
  );
};

// =====================================================
// CÓDIGO DEL PROGRAMA
// CAMPO CORRECTO: codPrograma
// =====================================================
const obtenerCodigoPrograma = (
  programa = {}
) => {
  return programa.codPrograma || "Sin código";
};

// =====================================================
// NIVEL DEL PROGRAMA
// SE CONSERVA SIN MODIFICACIONES
// =====================================================
const obtenerNivelPrograma = (
  programa = {}
) => {
  return (
    programa.nivelFormacion ||
    programa.nivelPrograma ||
    programa.nivel ||
    "Sin nivel"
  );
};

const ListarProgramaInstructores = () => {
  const [
    asignaciones,
    setAsignaciones,
  ] = useState([]);

  const [loading, setLoading] =
    useState(false);

  const [
    deletingId,
    setDeletingId,
  ] = useState(null);

  const navigate = useNavigate();

  // =====================================================
  // CONFIGURACIÓN DEL TOKEN
  // =====================================================
  const authConfig = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${localStorage.getItem(
          "token"
        )}`,
      },
    }),
    []
  );

  // =====================================================
  // CONTROLAR SESIÓN EXPIRADA
  // =====================================================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");

          navigate("/login", {
            replace: true,
          });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // =====================================================
  // PERMISOS DEL MÓDULO
  // =====================================================
  const moduloProgramaInstructores =
    useMemo(
      () =>
        buscarModuloPorNombre(
          MODULO
        ),
      []
    );

  const puedeCrear =
    moduloProgramaInstructores
      ?.puedeCrear === true ||
    moduloProgramaInstructores
      ?.crear === true ||
    moduloProgramaInstructores
      ?.create === true;

  const puedeActualizar =
    moduloProgramaInstructores
      ?.puedeActualizar === true ||
    moduloProgramaInstructores
      ?.actualizar === true ||
    moduloProgramaInstructores
      ?.editar === true ||
    moduloProgramaInstructores
      ?.update === true;

  const puedeEliminar =
    moduloProgramaInstructores
      ?.puedeEliminar === true ||
    moduloProgramaInstructores
      ?.eliminar === true ||
    moduloProgramaInstructores
      ?.delete === true;

  // =====================================================
  // CONSULTAR LOS PROGRAMAS ASIGNADOS
  // =====================================================
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        "/api/programainstructores/mis-programas",
        authConfig()
      );

      const lista = Array.isArray(
        data?.asignaciones
      )
        ? data.asignaciones
        : [];

      setAsignaciones(
        [...lista].reverse()
      );
    } catch (error) {
      console.error(
        "Error al cargar asignaciones:",
        error
      );

      if (manejarErrorSesion(error)) {
        return;
      }

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "No se pudo cargar la lista de asignaciones.",
        "error"
      );

      setAsignaciones([]);
    } finally {
      setLoading(false);
    }
  }, [
    authConfig,
    manejarErrorSesion,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  // =====================================================
  // ABRIR LAS GUÍAS DEL PROGRAMA
  // =====================================================
  const abrirGuiasPrograma = useCallback(
    (programa = {}) => {
      const codPrograma =
        programa.codPrograma;

      if (!codPrograma) {
        Swal.fire(
          "Código no disponible",
          "El programa no tiene un codPrograma registrado.",
          "warning"
        );

        return;
      }

      navigate(
        `/institucional/guias/listarguias/${encodeURIComponent(
          codPrograma
        )}`
      );
    },
    [navigate]
  );

  // =====================================================
  // EDITAR ASIGNACIÓN
  // =====================================================
  const editAsignacion = useCallback(
    (item) => {
      const programa =
        typeof item.programaObjectId ===
        "object"
          ? item.programaObjectId || {}
          : {};

      const instructor =
        typeof item.instructorObjectId ===
        "object"
          ? item.instructorObjectId || {}
          : {};

      const programaObjectId =
        programa._id ||
        (typeof item.programaObjectId ===
        "string"
          ? item.programaObjectId
          : "");

      const instructorObjectId =
        instructor._id ||
        (typeof item.instructorObjectId ===
        "string"
          ? item.instructorObjectId
          : "");

      const nombreInstructor = [
        instructor.nombre1Instructor,
        instructor.nombre2Instructor,
        instructor.apellido1Instructor,
        instructor.apellido2Instructor,
      ]
        .filter(Boolean)
        .join(" ");

      localStorage.setItem(
        "programaInstructorEdit",
        JSON.stringify({
          _id: item._id,

          programaObjectId,

          programaLabel: `${obtenerCodigoPrograma(
            programa
          )} - ${obtenerNombrePrograma(
            programa
          )}`,

          instructorObjectId,

          instructorLabel: `${
            nombreInstructor ||
            "Instructor no disponible"
          } - ${
            instructor.documentoInstructor ||
            "Sin documento"
          }`,

          rolInstructor:
            item.rolInstructor ||
            "LIDER",

          estadoAsignacion:
            item.estadoAsignacion ||
            "ACTIVO",

          observacionAsignacion:
            item.observacionAsignacion ||
            "",
        })
      );

      navigate(
        `/programainstructores/editar/${item._id}`
      );
    },
    [navigate]
  );

  // =====================================================
  // ELIMINAR ASIGNACIÓN
  // =====================================================
  const deleteAsignacion = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar asignaciones.",
          "warning"
        );

        return;
      }

      const result = await Swal.fire({
        title: "¿Está seguro?",
        text: "El instructor será retirado del programa de formación.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        setDeletingId(id);

        const { data } = await axios.delete(
          `/api/programainstructores/${id}`,
          authConfig()
        );

        setAsignaciones((prev) =>
          prev.filter(
            (asignacion) =>
              asignacion._id !== id
          )
        );

        Swal.fire({
          icon: "success",
          title:
            data?.message ||
            "Asignación eliminada",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error(
          "Error al eliminar asignación:",
          error
        );

        if (manejarErrorSesion(error)) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data?.message ||
            "No se pudo eliminar la asignación.",
          "error"
        );
      } finally {
        setDeletingId(null);
      }
    },
    [
      puedeEliminar,
      authConfig,
      manejarErrorSesion,
    ]
  );

  // =====================================================
  // PREPARAR DATOS PARA LA TABLA
  // =====================================================
  const dataTabla = useMemo(
    () =>
      asignaciones.map((item) => {
        const instructor =
          typeof item.instructorObjectId ===
          "object"
            ? item.instructorObjectId || {}
            : {};

        const programa =
          typeof item.programaObjectId ===
          "object"
            ? item.programaObjectId || {}
            : {};

        const nombreCompleto = [
          instructor.nombre1Instructor,
          instructor.nombre2Instructor,
          instructor.apellido1Instructor,
          instructor.apellido2Instructor,
        ]
          .filter(Boolean)
          .join(" ");

        const estadoAsignacion =
          String(
            item.estadoAsignacion || ""
          )
            .trim()
            .toUpperCase();

        const nombrePrograma =
          obtenerNombrePrograma(
            programa
          );

        const codPrograma =
          programa.codPrograma || "";

        return {
          ...item,

          documentoInstructor:
            instructor.documentoInstructor ||
            "Sin documento",

          instructorNombre: (
            <div title={nombreCompleto}>
              {nombreCompleto ||
                "Instructor no disponible"}
            </div>
          ),

          telefonoInstructor:
            instructor.telefono1Instructor ||
            "Sin teléfono",

          codigoPrograma:
            obtenerCodigoPrograma(
              programa
            ),

          nombrePrograma: (
            <div
              title={nombrePrograma}
              style={{
                width: "260px",
                minWidth: "260px",
                maxWidth: "260px",

                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere",

                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,

                overflow: "hidden",
                textOverflow: "ellipsis",

                lineHeight: "1.25rem",
                maxHeight: "2.5rem",
              }}
            >
              {nombrePrograma}
            </div>
          ),

          nivelPrograma:
            obtenerNivelPrograma(
              programa
            ),

          rolInstructorVista: (() => {
            const estiloRol =
              obtenerEstiloRolInstructor(
                item.rolInstructor
              );

            return (
              <span
                className="badge d-inline-flex align-items-center gap-2 px-3 py-2"
                style={{
                  backgroundColor:
                    estiloRol.backgroundColor,
                  color:
                    estiloRol.color,
                  fontSize: "0.78rem",
                  letterSpacing:
                    "0.03rem",
                  minWidth: 105,
                  justifyContent:
                    "center",
                }}
                title={`Rol del instructor: ${estiloRol.etiqueta}`}
              >
                <i
                  className={
                    estiloRol.icono
                  }
                ></i>

                {estiloRol.etiqueta}
              </span>
            );
          })(),

          estadoAsignacionVista: (
            <span
              className={`badge ${
                estadoAsignacion ===
                "ACTIVO"
                  ? "bg-success"
                  : "bg-secondary"
              }`}
            >
              {estadoAsignacion ||
                "SIN ESTADO"}
            </span>
          ),

          funciones: (
            <div className="d-flex gap-2 flex-nowrap justify-content-end">

              {/* =====================================
                  BOTÓN GUÍAS

                  Se muestra directamente porque el
                  usuario ya tiene acceso a esta vista.

                  La autorización definitiva debe
                  validarse también en el backend.
              ===================================== */}

              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  flexShrink: 0,
                }}
                disabled={
                  loading ||
                  !codPrograma
                }
                onClick={() =>
                  abrirGuiasPrograma(
                    programa
                  )
                }
                title={
                  codPrograma
                    ? "Listar guías del programa"
                    : "El programa no tiene codPrograma"
                }
                type="button"
              >
                <i className="fa-solid fa-list-check"></i>
              </button>

              {/* =====================================
                  EDITAR
              ===================================== */}

              {puedeActualizar && (
                <button
                  className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 34,
                    height: 34,
                    minWidth: 34,
                    flexShrink: 0,
                  }}
                  disabled={
                    loading ||
                    deletingId ===
                      item._id
                  }
                  onClick={() =>
                    editAsignacion(item)
                  }
                  title="Editar asignación"
                  type="button"
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
              )}

              {/* =====================================
                  ELIMINAR
              ===================================== */}

              {puedeEliminar && (
                <button
                  className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 34,
                    height: 34,
                    minWidth: 34,
                    flexShrink: 0,
                  }}
                  disabled={
                    loading ||
                    deletingId ===
                      item._id
                  }
                  onClick={() =>
                    deleteAsignacion(
                      item._id
                    )
                  }
                  title="Eliminar asignación"
                  type="button"
                >
                  {deletingId ===
                  item._id ? (
                    <i className="fa-solid fa-spinner fa-spin text-white"></i>
                  ) : (
                    <i className="fa-solid fa-trash"></i>
                  )}
                </button>
              )}
            </div>
          ),
        };
      }),
    [
      asignaciones,
      loading,
      deletingId,
      puedeActualizar,
      puedeEliminar,
      abrirGuiasPrograma,
      editAsignacion,
      deleteAsignacion,
    ]
  );

  // =====================================================
  // COLUMNAS DE LA TABLA
  // =====================================================
  const columns = useMemo(
    () => [
      {
        Header: "Documento",
        accessor:
          "documentoInstructor",
      },
      {
        Header: "Instructor",
        accessor:
          "instructorNombre",
      },
      {
        Header: "Teléfono",
        accessor:
          "telefonoInstructor",
      },
      {
        Header: "Código",
        accessor:
          "codigoPrograma",
      },
      {
        Header:
          "Programa de formación",
        accessor:
          "nombrePrograma",
      },
      {
        Header: "Nivel",
        accessor:
          "nivelPrograma",
      },
      {
        Header: "Rol",
        accessor:
          "rolInstructorVista",
      },
      {
        Header: "Estado",
        accessor:
          "estadoAsignacionVista",
      },
      {
        Header: "Acciones",
        accessor: "funciones",
      },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

          {/* =================================================
              ENCABEZADO
          ================================================= */}

          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Instructores por Programa
                </h5>

                <small className="text-secondary">
                  Gestión de instructores asociados
                  a programas de formación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">

                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() =>
                    navigate("/inicio")
                  }
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      localStorage.removeItem(
                        "programaInstructorEdit"
                      );

                      navigate(
                        "/programainstructores/registrar"
                      );
                    }}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* =================================================
              CONTENIDO
          ================================================= */}

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                ></div>

                <div className="mt-3">
                  Cargando asignaciones…
                </div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable
                  columns={columns}
                  data={dataTabla}
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">

                <div className="display-6 mb-2">
                  👨‍🏫
                </div>

                <div className="fw-semibold">
                  No hay instructores asignados
                </div>

                <div className="small mb-3">
                  Crea la primera asignación para
                  relacionar instructores con programas
                  de formación.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => {
                      localStorage.removeItem(
                        "programaInstructorEdit"
                      );

                      navigate(
                        "/programainstructores/registrar"
                      );
                    }}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear asignación
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarProgramaInstructores;